import dayjs from 'dayjs';
import { formatCurrency, formatDate, formatNumber, parseNumber } from 'helper';
import { each, times } from 'lodash';

import * as c from './constants';

export const transformDashboardHistogram = () => (raw) => {
  const list = [];
  (raw?.data || []).forEach((row) => {
    const formatted = {
      label: row?.label,
      transaction: formatNumber(row?.count, 0) || '-',
      amount: formatNumber(row?.amount) || '-',
    };
    list.push(formatted);
  });
  return { list };
};

export const transformDashboardScheme = () => (raw) => {
  const list = [];
  (raw?.data || []).forEach((row, id) => {
    const formatted = {
      id,
      label: row?.label,
      transaction: formatNumber(row?.count, 0) || '-',
      count: formatNumber(row.count, 0) || '-',
      amount: formatNumber(row?.amount) || '-',
    };
    list.push(formatted);
  });
  return { list };
};

const hours = Array.from(
  {
    length: 24,
  },
  (_, hour) => dayjs().hour(hour).format('hh:00 A')
);

const daily = (start_date) => {
  const year = dayjs(start_date).get('year');
  const month = dayjs(start_date).get('month');
  const date = new Date(year, month, 1);
  const monthDate = dayjs(start_date).startOf('month'); // change to a date in the month of interest

  const daysInMonth = [];

  times(monthDate.daysInMonth(), () => {
    daysInMonth.push(dayjs(date).format('DD'));
    date.setDate(date.getDate() + 1);
  });

  return daysInMonth;
};

const months = Array.from(
  {
    length: 12,
  },
  (_, month) => dayjs().month(month).format('MMM')
);

const formatKey = (key, value) =>
  ({
    day: formatDate(value, 'DD/MM/YYYY'),
    month: formatDate(value, 'MMMM'),
    year: formatDate(value, 'YYYY'),
  }[key]);

export const transformCompareTable = (mode, items) => (raw) => {
  const column = {};
  (raw?.data?.compare || []).forEach((col, id) => {
    const index = formatKey(mode, items[id]);
    const columnKey = c.buildId(items[id], mode);
    column[columnKey] = {};
    (col[index] || []).forEach((row) => {
      const rowKey = row?.label;
      column[columnKey][rowKey] = {
        label: row?.label,
        transaction: row?.count,
      };
    });
  });
  return column;
};

export const transformCompareChart = (mode, items) => (raw) => {
  const list = [];
  each(raw?.data?.compare, (col, id) => {
    list.push({});
    each(col, (row, index) => {
      list[id][index] = [];
      if (mode === 'day') {
        hours.forEach((x) => {
          const findItem = row.find(({ label }) => label === x);
          const formatted = {
            label: findItem?.label || x,
            transaction: findItem?.count ? parseNumber(findItem?.count) : 0,
            row: findItem?.label || x,
          };
          list[id][index].push(formatted);
        });
      }
      if (mode === 'month') {
        daily(items[id]).forEach((x) => {
          const findItem = row.find(({ label }) => label === x);
          const formatted = {
            label: findItem?.label || x,
            transaction: findItem?.count ? parseNumber(findItem?.count) : 0,
            row: findItem?.label || x,
          };
          list[id][index].push(formatted);
        });
      }
      if (mode === 'year') {
        months.forEach((x) => {
          const findItem = row.find(({ label }) => label === x);
          const formatted = {
            label: findItem?.label || x,
            transaction: findItem?.count ? parseNumber(findItem?.count) : 0,
            row: findItem?.label || x,
          };
          list[id][index].push(formatted);
        });
      }
    });
  });
  return list;
};

export const transformTransaction = (raw) => {
  return {
    id: raw?.id,
    created_at: raw?.created_at,
    client_refno: raw?.client_reference_number,
    order_id: raw?.order_id,
    biller_name: raw?.biller_name,
    refno: raw?.reference_number,
    entry: raw?.entry,
    type: raw?.type,
    amount: raw?.amount,
    amount_human: formatCurrency(parseNumber(raw?.amount, 0)),
    payment_status: raw?.payment_status,
    remarks: raw?.remarks,
    user_uuid: raw?.user_uuid,
    uuid: raw?.uuid,
    account_number: raw?.account_number,
    account_name: raw?.account_name,
    account_email: raw?.account_email,
    transaction_fee: raw?.transaction_fee,
    transaction_fee_human: formatCurrency(parseNumber(raw?.transaction_fee, 0)),
    other_charges: raw?.other_charges,
    other_charges_human: formatCurrency(parseNumber(raw?.other_charges, 0)),
    net_amount: raw?.net_amount,
    net_amount_human: formatCurrency(parseNumber(raw?.net_amount, 0)),
    cashback_amount_human: formatCurrency(parseNumber(raw?.cashback_amount, 0)),
    fee_type: raw?.fee_type,
    is_dispute: !!raw?.is_dispute,
  };
};

export const transformSelectedTransaction = (raw) => {
  const request = (raw?.request || []).filter(
    (item) =>
      item?.key !== 'account_number' &&
      item?.key !== 'biller_code' &&
      item?.key !== 'subscription_type' &&
      item?.key !== 'amount'
  );
  return {
    uuid: raw?.uuid,
    user_uuid: raw?.user_uuid,
    biller_name: raw?.biller_name,
    account_number: raw?.account_number,
    account_name: raw?.account_name,
    account_email: raw?.account_email,
    client_refno: raw?.client_reference_number,
    refno: raw?.reference_number,
    order_id: raw?.order_id,
    amount: raw?.amount,
    payment_status: raw?.payment_status,
    created_at: raw?.created_at,
    transaction_fee: formatNumber(raw?.transaction_fee, 2),
    other_charges: formatNumber(raw?.other_charges, 2),
    net_amount: formatNumber(raw?.net_amount, 2),
    fee_type: raw?.fee_type,
    request,
    is_dispute: !!raw?.is_dispute,
  };
};

export const transformCountCards = (raw) => ({
  total_amount: `$${raw?.total_amount}`,
  total_transactions: raw?.total_transactions,
  payouts: `$${formatNumber(raw?.payouts, 2)}`,
  accumulated_amount: `$${formatNumber(raw?.accumulated_amount, 2)}`,
});

export const transformTransactionHistogram = (raw) => {
  const data = (raw?.data || []).map((item, i) => ({
    id: `${i}_${item?.label}`,
    label: item?.label,
    count: parseNumber(item?.count),
    amount: item?.amount,
  }));
  return data;
};

export const transformAmtHistogram = (raw) => {
  const data = (raw?.data || []).map((item, i) => ({
    id: `${i}_${item?.label}`,
    label: item?.label,
    amount: parseNumber(item?.amount?.replace('$', '')),
    amount_human: item?.amount,
  }));
  return data;
};

export const transformComparativeTransaction = (raw) => {
  const table = [];
  const graph = [];
  each(raw, (row, id) => {
    table.push({ [id]: [] });
    graph.push({ name: id, data: [] });
    const index = table.length - 1;
    months.forEach((x) => {
      const findItem = row.find(({ label }) => label === x);
      const formatted = {
        label: findItem?.label || x,
        transaction: findItem?.count ? parseNumber(findItem?.count) : 0,
        row: findItem?.label || x,
      };
      table[index][id].push(formatted);
      graph[index].data.push(
        findItem?.count ? parseNumber(findItem?.count) : 0
      );
    });
  });
  return { table, graph };
};

export const transformOrganizationCountCards = (raw) => ({
  total_amount: `$${raw?.total_amount}`,
  total_transactions: raw?.total_transactions,
  inactive_billers: raw?.inactive_billers,
  active_billers: raw?.active_billers,
});

export const transformOrganizationTopBiller = (raw) => {
  const table = [];
  const chart = [];
  if (raw?.data.length !== 0) {
    raw?.data.forEach((item, index) => {
      table.push({ rank: index + 1, ...item });
      chart.push({
        id: item?.index + 1,
        title: item?.biller_name,
        count: item.total_transactions,
        label: item?.biller_name,
      });
    });
    const formattedTable = table.slice(0, 5);
    const formattedChart = chart.slice(0, 5);
    return { table: formattedTable, chart: formattedChart };
  }
  return { table, chart };
};

export const transformOrganizationTopBillerType = (raw) => {
  const table = [];
  const chart = [];

  if (raw?.data.length !== 0) {
    const data = raw?.data.sort((a, b) => {
      return b?.total_transactions - a?.total_transactions;
    });
    data.forEach((item, index) => {
      table.push({
        rank: index + 1,
        ...item,
        biller_type: item?.biller_type ? c.BILLER_TYPE[item?.biller_type] : '',
      });
      chart.push({
        id: item?.index + 1,
        title: item?.biller_type ? c.BILLER_TYPE[item?.biller_type] : '',
        count: item?.total_transactions,
        label: item?.biller_type ? c.BILLER_TYPE[item?.biller_type] : '',
        amount: item?.total_amount,
      });
    });
    const formattedTable = table.slice(0, 5);
    const formattedChart = chart.slice(0, 5);
    return { table: formattedTable, chart: formattedChart };
  }
  return { table, chart };
};

export const transformDisputePayload = (payload) => ({
  remarks: payload?.remarks,
  ref_no: payload?.refno,
});
